import { createApi } from "@reduxjs/toolkit/query/react";
import { createAuthBaseQuery } from "../auth/reauthBaseQuery";
import { appConfig } from "../../appConfig";

import {
  FilteredPagedModel,
  FilteredPagedQueryResult,
  EntityCommonInfoModel,
  CommonMultiDeleteModel,
  CommonEntityActiveSwitch,
  CommonRejectionModel,
} from "../../entities/common";

import { getQuerySearchParams } from "../../utils";
import {
  AddAuthorToGroupModel,
  ArticleCategoryModel,
  ArticleCommonInfoModel,
  ArticleContractModel,
  ArticleModel,
  ArticleReasonModel,
  ArticleSubCategoryModel,
  AuthorCooperationRequestModel,
  AuthorGroupModel,
  AuthorModel,
  AuthorRelModel,
  BankAccountModel,
} from "../../entities/articles";

export const TagTypes = {
  ArticleCategory: "ArticleCategory",
  ArticleSubCategory: "ArticleSubCategory",
  Author: "Author",
  AuthorGroup: "AuthorGroup",
  Article: "Article",
  ArticleDurationType: "ArticleDurationType",
  AddAuthorToGroup: "AddAuthorToGroup",
  BankAccount: "BankAccount",
  AuthorCooperationRequest: "AuthorCooperationRequest",
  ArticleContract: "ArticleContract",
  ArticleSubscription: "ArticleSubscription",
  ArticleInvoice: "ArticleInvoice",
};

export const articlesSlice = createApi({
  reducerPath: "articles",
  baseQuery: createAuthBaseQuery(`${appConfig.articlesUrl}/api`),
  tagTypes: Object.values(TagTypes),

  endpoints: (builder) => ({
    // ArticleCategory
    getArticleCategories: builder.query<ArticleCategoryModel[], undefined>({
      query: () => "/articleCategory",
      providesTags: () => [{ type: TagTypes.ArticleCategory, id: "LIST" }],
    }),
    getArticleCategoryCommonInfo: builder.query<EntityCommonInfoModel, undefined>({
      query: () => "/articleCategory/commonInfo",
      providesTags: [{ type: TagTypes.ArticleCategory, id: "LIST" }],
    }),
    getArticleCategoriesFilteredPaged: builder.query<FilteredPagedQueryResult<ArticleCategoryModel>, FilteredPagedModel>({
      query: ({ page = 1, pageSize = 10, searchTerm = "", sortFieldName = "", descending = false, ...rest }: FilteredPagedModel) => {
        let url = `/articleCategory/pageable?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}&sortFieldName=${sortFieldName}&descending=${descending}`;
        const restParams = getQuerySearchParams(rest);
        if (restParams) {
          url += `&${restParams}`;
        }

        return url;
      },
      providesTags: (result, error, arg) =>
        result
          ? [
              { type: TagTypes.ArticleCategory, id: "PARTIAL-LIST" },
              ...result.data.map(({ id }: { id: number | string }) => ({
                type: TagTypes.ArticleCategory,
                id: Number(id),
              })),
            ]
          : [{ type: TagTypes.ArticleCategory, id: "PARTIAL-LIST" }],
    }),
    getArticleCategory: builder.query<ArticleCategoryModel, number>({
      query: (categoryId) => `/articleCategory/${categoryId}`,
      providesTags: (result, error, arg) => [{ type: TagTypes.ArticleCategory, id: arg }],
    }),
    addArticleCategory: builder.mutation({
      query: (initialArticleCategory) => ({
        url: "/articleCategory",
        method: "POST",
        body: initialArticleCategory,
      }),
      invalidatesTags: [
        { type: TagTypes.ArticleCategory, id: "LIST" },
        { type: TagTypes.ArticleCategory, id: "PARTIAL-LIST" },
      ],
    }),
    editArticleCategory: builder.mutation({
      query: (articleCategory) => ({
        url: `/articleCategory/${articleCategory.id}`,
        method: "PUT",
        body: articleCategory,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: TagTypes.ArticleCategory, id: Number(arg.id) },
        { type: TagTypes.ArticleCategory, id: "LIST" },
        { type: TagTypes.ArticleCategory, id: "PARTIAL-LIST" },
      ],
    }),
    deleteArticleCategory: builder.mutation({
      query: (categoryId: number) => ({
        url: `/articleCategory/${categoryId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              { type: TagTypes.ArticleCategory, id: arg },
              { type: TagTypes.ArticleCategory, id: "LIST" },
              { type: TagTypes.ArticleCategory, id: "PARTIAL-LIST" },
            ],
    }),
    deleteMultiArticleCategories: builder.mutation({
      query: (multiDeleteModel: CommonMultiDeleteModel) => ({
        url: "/articleCategory/multiDelete",
        method: "DELETE",
        body: multiDeleteModel,
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              ...arg.ids.map((id: number) => ({
                type: TagTypes.ArticleCategory,
                id,
              })),
              { type: TagTypes.ArticleCategory, id: "LIST" },
              { type: TagTypes.ArticleCategory, id: "PARTIAL-LIST" },
            ],
    }),

    // ArticleSubCategory
    getArticleSubCategories: builder.query<ArticleSubCategoryModel[], undefined>({
      query: () => "/articleSubCategory",
      providesTags: (result = [], error, arg) => [{ type: TagTypes.ArticleSubCategory, id: "LIST" }],
    }),
    getArticleSubCategoryCommonInfo: builder.query<EntityCommonInfoModel, undefined>({
      query: () => "/articleSubCategory/commonInfo",
      providesTags: [{ type: TagTypes.ArticleSubCategory, id: "LIST" }],
    }),
    getArticleSubCategoriesFilteredPaged: builder.query<FilteredPagedQueryResult<ArticleSubCategoryModel>, FilteredPagedModel>({
      query: ({ page = 1, pageSize = 10, searchTerm = "", sortFieldName = "", descending = false, ...rest }: FilteredPagedModel) => {
        let url = `/articleSubCategory/pageable?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}&sortFieldName=${sortFieldName}&descending=${descending}`;
        const restParams = getQuerySearchParams(rest);
        if (restParams) {
          url += `&${restParams}`;
        }

        return url;
      },
      providesTags: (result, error, arg) =>
        result
          ? [
              { type: TagTypes.ArticleSubCategory, id: "PARTIAL-LIST" },
              ...result.data.map(({ id }: { id: number | string }) => ({
                type: TagTypes.ArticleSubCategory,
                id: Number(id),
              })),
            ]
          : [{ type: TagTypes.ArticleSubCategory, id: "PARTIAL-LIST" }],
    }),
    getArticleSubCategory: builder.query<ArticleSubCategoryModel, number>({
      query: (subCategoryId) => `/articleSubCategory/${subCategoryId}`,
      providesTags: (result, error, arg) => [{ type: TagTypes.ArticleSubCategory, id: arg }],
    }),
    addArticleSubCategory: builder.mutation({
      query: (initialArticleSubCategory) => ({
        url: "/articleSubCategory",
        method: "POST",
        body: initialArticleSubCategory,
      }),
      invalidatesTags: [
        { type: TagTypes.ArticleSubCategory, id: "LIST" },
        { type: TagTypes.ArticleSubCategory, id: "PARTIAL-LIST" },
        { type: TagTypes.ArticleCategory, id: "LIST" },
        { type: TagTypes.ArticleCategory, id: "PARTIAL-LIST" },
      ],
    }),
    editArticleSubCategory: builder.mutation({
      query: (articleSubCategory) => ({
        url: `/articleSubCategory/${articleSubCategory.id}`,
        method: "PUT",
        body: articleSubCategory,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: TagTypes.ArticleSubCategory, id: Number(arg.id) },
        { type: TagTypes.ArticleCategory, id: "LIST" },
        { type: TagTypes.ArticleSubCategory, id: "PARTIAL-LIST" },
        { type: TagTypes.ArticleCategory, id: "LIST" },
        { type: TagTypes.ArticleCategory, id: "PARTIAL-LIST" },
      ],
    }),
    deleteArticleSubCategory: builder.mutation({
      query: (subCategoryId: number) => ({
        url: `/articleSubCategory/${subCategoryId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              { type: TagTypes.ArticleSubCategory, id: arg },
              { type: TagTypes.ArticleSubCategory, id: "LIST" },
              { type: TagTypes.ArticleSubCategory, id: "PARTIAL-LIST" },
              { type: TagTypes.ArticleCategory, id: "LIST" },
              { type: TagTypes.ArticleCategory, id: "PARTIAL-LIST" },
            ],
    }),
    deleteMultiArticleSubCategories: builder.mutation({
      query: (multiDeleteModel: CommonMultiDeleteModel) => ({
        url: "/articleSubCategory/multiDelete",
        method: "DELETE",
        body: multiDeleteModel,
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              ...arg.ids.map((id: number) => ({
                type: TagTypes.ArticleSubCategory,
                id,
              })),
              { type: TagTypes.ArticleSubCategory, id: "LIST" },
              { type: TagTypes.ArticleSubCategory, id: "PARTIAL-LIST" },
              { type: TagTypes.ArticleCategory, id: "LIST" },
              { type: TagTypes.ArticleCategory, id: "PARTIAL-LIST" },
            ],
    }),

    // Author
    getAuthors: builder.query<AuthorModel[], undefined>({
      query: () => "/author",
      providesTags: (result = [], error, arg) => [{ type: TagTypes.Author, id: "LIST" }],
    }),
    getAuthorCommonInfo: builder.query<EntityCommonInfoModel, undefined>({
      query: () => "/author/commonInfo",
      providesTags: [{ type: TagTypes.Author, id: "LIST" }],
    }),
    getAuthorsFilteredPaged: builder.query<FilteredPagedQueryResult<AuthorModel>, FilteredPagedModel>({
      query: ({ page = 1, pageSize = 10, searchTerm = "", sortFieldName = "", descending = false, ...rest }: FilteredPagedModel) => {
        let url = `/author/pageable?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}&sortFieldName=${sortFieldName}&descending=${descending}`;
        const restParams = getQuerySearchParams(rest);
        if (restParams) {
          url += `&${restParams}`;
        }

        return url;
      },
      providesTags: (result, error, arg) =>
        result
          ? [
              { type: TagTypes.Author, id: "PARTIAL-LIST" },
              ...result.data.map(({ id }: { id: number | string }) => ({
                type: TagTypes.Author,
                id: Number(id),
              })),
            ]
          : [{ type: TagTypes.Author, id: "PARTIAL-LIST" }],
    }),
    getAuthor: builder.query<AuthorModel, number>({
      query: (authorId) => `/author/${authorId}`,
      providesTags: (result, error, arg) => [{ type: TagTypes.Author, id: arg }],
    }),
    addAuthor: builder.mutation({
      query: (initialAuthor) => ({
        url: "/Author",
        method: "POST",
        body: initialAuthor,
      }),
      invalidatesTags: [
        { type: TagTypes.Author, id: "LIST" },
        { type: TagTypes.Author, id: "PARTIAL-LIST" },
      ],
    }),
    editAuthor: builder.mutation({
      query: (author) => ({
        url: `/author/${author.id}`,
        method: "PUT",
        body: author,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: TagTypes.Author, id: Number(arg.id) },
        { type: TagTypes.Author, id: "PARTIAL-LIST" },
        { type: TagTypes.Author, id: "LIST" },
      ],
    }),
    switchActiveAuthor: builder.mutation({
      query: (model: CommonEntityActiveSwitch) => ({
        url: `/author/switchActive/${model.id}`,
        method: "PUT",
        body: model,
      }),
      invalidatesTags: (result, error, arg) => [{ type: TagTypes.Author, id: arg.id }],
    }),
    deleteAuthor: builder.mutation({
      query: (authorId: number) => ({
        url: `/author/${authorId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              { type: TagTypes.Author, id: arg },
              { type: TagTypes.Author, id: "LIST" },
              { type: TagTypes.AuthorGroup, id: "PARTIAL-LIST" },
              { type: TagTypes.AuthorGroup, id: "LIST" },
            ],
    }),
    deleteMultiAuthors: builder.mutation({
      query: (multiDeleteModel: CommonMultiDeleteModel) => ({
        url: "/author/multiDelete",
        method: "DELETE",
        body: multiDeleteModel,
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              ...arg.ids.map((id: number) => ({
                type: TagTypes.Author,
                id,
              })),
              { type: TagTypes.Author, id: "LIST" },
              { type: TagTypes.AuthorGroup, id: "PARTIAL-LIST" },
              { type: TagTypes.AuthorGroup, id: "LIST" },
            ],
    }),

    // AuthorGroup
    getAuthorGroups: builder.query<AuthorGroupModel[], undefined>({
      query: () => "/authorGroup",
      providesTags: [{ type: TagTypes.AuthorGroup, id: "LIST" }],
    }),
    getAuthorGroupCommonInfo: builder.query<EntityCommonInfoModel, undefined>({
      query: () => "/authorGroup/commonInfo",
      providesTags: [{ type: TagTypes.AuthorGroup, id: "LIST" }],
    }),
    getAuthorGroupsFilteredPaged: builder.query<FilteredPagedQueryResult<AuthorGroupModel>, FilteredPagedModel>({
      query: ({ page = 1, pageSize = 10, searchTerm = "", sortFieldName = "", descending = false, ...rest }: FilteredPagedModel) => {
        let url = `/authorGroup/pageable?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}&sortFieldName=${sortFieldName}&descending=${descending}`;
        const restParams = getQuerySearchParams(rest);
        if (restParams) {
          url += `&${restParams}`;
        }

        return url;
      },
      providesTags: (result, error, arg) =>
        result
          ? [
              { type: TagTypes.AuthorGroup, id: "PARTIAL-LIST" },
              ...result.data.map(({ id }: { id: number | string }) => ({
                type: TagTypes.AuthorGroup,
                id: Number(id),
              })),
            ]
          : [{ type: TagTypes.AuthorGroup, id: "PARTIAL-LIST" }],
    }),
    getAuthorGroup: builder.query<AuthorGroupModel, number>({
      query: (authorGroupId) => `/authorGroup/${authorGroupId}`,
      providesTags: (result, error, arg) => [{ type: TagTypes.AuthorGroup, id: arg }],
    }),
    addAuthorGroup: builder.mutation({
      query: (initialAuthorGroup) => ({
        url: "/authorGroup",
        method: "POST",
        body: initialAuthorGroup,
      }),
      invalidatesTags: [
        { type: TagTypes.AuthorGroup, id: "LIST" },
        { type: TagTypes.AuthorGroup, id: "PARTIAL-LIST" },
        { type: TagTypes.Author, id: "LIST" },
        { type: TagTypes.Author, id: "PARTIAL-LIST" },
      ],
    }),
    editAuthorGroup: builder.mutation({
      query: (authorGroup) => ({
        url: `/authorGroup/${authorGroup.id}`,
        method: "PUT",
        body: authorGroup,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: TagTypes.AuthorGroup, id: Number(arg.id) },
        { type: TagTypes.AuthorGroup, id: "LIST" },
        { type: TagTypes.Author, id: "LIST" },
        { type: TagTypes.Author, id: "PARTIAL-LIST" },
      ],
    }),
    switchActiveAuthorGroup: builder.mutation({
      query: (model: CommonEntityActiveSwitch) => ({
        url: `/authorGroup/switchActive/${model.id}`,
        method: "PUT",
        body: model,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: TagTypes.AuthorGroup, id: arg.id },
        { type: TagTypes.AuthorGroup, id: "LIST" },
      ],
    }),
    activateAuthorGroup: builder.mutation<AuthorGroupModel, number>({
      query: (authorGroupId) => ({
        url: `/authorGroup/activate/${authorGroupId}`,
        method: "PUT",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: TagTypes.AuthorGroup, id: Number(arg) },
        { type: TagTypes.AuthorGroup, id: "LIST" },
      ],
    }),
    rejectAuthorGroup: builder.mutation<AddAuthorToGroupModel, CommonRejectionModel>({
      query: (model) => ({
        url: `/authorGroup/reject/${model.id}`,
        method: "PUT",
        body: model,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: TagTypes.AuthorGroup, id: Number(arg.id) },
        { type: TagTypes.AuthorGroup, id: "LIST" },
      ],
    }),
    disableAuthorGroup: builder.mutation<AuthorGroupModel, number>({
      query: (authorGroupId) => ({
        url: `/authorGroup/disable/${authorGroupId}`,
        method: "PUT",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: TagTypes.AuthorGroup, id: Number(arg) },
        { type: TagTypes.AuthorGroup, id: "LIST" },
      ],
    }),
    addAuthorToGroup: builder.mutation<AuthorGroupModel, AuthorRelModel>({
      query: (model) => ({
        url: `/authorGroup/${model.authorGroupId}/author/add`,
        method: "PUT",
        body: model,
      }),
      invalidatesTags: (result, error, arg) =>
        result
          ? [
              { type: TagTypes.AuthorGroup, id: "LIST" },
              { type: TagTypes.AuthorGroup, id: result.id },
              { type: TagTypes.Author, id: "LIST" },
              { type: TagTypes.Author, id: "PARTIAL-LIST" },
            ]
          : [],
    }),
    removeAuthorFromGroup: builder.mutation<AuthorGroupModel, AuthorRelModel>({
      query: (model) => ({
        url: `/authorGroup/${model.authorGroupId}/author/remove`,
        method: "PUT",
        body: model,
      }),
      invalidatesTags: (result, error, arg) =>
        result
          ? [
              { type: TagTypes.AuthorGroup, id: "LIST" },
              { type: TagTypes.AuthorGroup, id: result.id },
              { type: TagTypes.Author, id: "LIST" },
              { type: TagTypes.Author, id: "PARTIAL-LIST" },
            ]
          : [],
    }),
    deleteAuthorGroup: builder.mutation({
      query: (authorGroupId: number) => ({
        url: `/authorGroup/${authorGroupId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              { type: TagTypes.AuthorGroup, id: arg },
              { type: TagTypes.AuthorGroup, id: "LIST" },
              { type: TagTypes.Author, id: "LIST" },
              { type: TagTypes.Author, id: "PARTIAL-LIST" },
            ],
    }),
    deleteMultiAuthorGroups: builder.mutation({
      query: (multiDeleteModel: CommonMultiDeleteModel) => ({
        url: "/authorGroup/multiDelete",
        method: "DELETE",
        body: multiDeleteModel,
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              ...arg.ids.map((id: number) => ({
                type: TagTypes.AuthorGroup,
                id,
              })),
              { type: TagTypes.AuthorGroup, id: "LIST" },
              { type: TagTypes.Author, id: "LIST" },
              { type: TagTypes.Author, id: "PARTIAL-LIST" },
            ],
    }),

    // AddAuthorToGroupRequest

    getAddAuthorToGroupRequestCommonInfo: builder.query<EntityCommonInfoModel, undefined>({
      query: () => "/authorGroup/author/addRequest/commonInfo",
      providesTags: [{ type: TagTypes.AddAuthorToGroup, id: "LIST" }],
    }),
    getAddAuthorToGroupRequestsFilteredPaged: builder.query<FilteredPagedQueryResult<AddAuthorToGroupModel>, FilteredPagedModel>({
      query: ({ page = 1, pageSize = 10, searchTerm = "", sortFieldName = "", descending = false, ...rest }: FilteredPagedModel) => {
        let url = `/authorGroup/author/addRequest/pageable?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}&sortFieldName=${sortFieldName}&descending=${descending}`;
        const restParams = getQuerySearchParams(rest);
        if (restParams) {
          url += `&${restParams}`;
        }

        return url;
      },
      providesTags: (result, error, arg) =>
        result
          ? [
              { type: TagTypes.AddAuthorToGroup, id: "PARTIAL-LIST" },
              ...result.data.map(({ id }: { id: number | string }) => ({
                type: TagTypes.AddAuthorToGroup,
                id: Number(id),
              })),
            ]
          : [{ type: TagTypes.AddAuthorToGroup, id: "PARTIAL-LIST" }],
    }),
    getAddAuthorToGroupRequest: builder.query<AddAuthorToGroupModel, number>({
      query: (requestId) => `/authorGroup/author/addRequest/${requestId}`,
      providesTags: (result, error, arg) => [{ type: TagTypes.AddAuthorToGroup, id: arg }],
    }),
    addAddAuthorToGroupRequest: builder.mutation<AddAuthorToGroupModel, AddAuthorToGroupModel>({
      query: (model) => ({
        url: "/authorGroup/author/addRequest",
        method: "POST",
        body: model,
      }),
      invalidatesTags: [
        { type: TagTypes.AddAuthorToGroup, id: "LIST" },
        { type: TagTypes.AddAuthorToGroup, id: "PARTIAL-LIST" },
      ],
    }),
    editAddAuthorToGroupRequest: builder.mutation<AddAuthorToGroupModel, AddAuthorToGroupModel>({
      query: (model) => ({
        url: `/authorGroup/author/addRequest/${model.id}`,
        method: "PUT",
        body: model,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: TagTypes.AddAuthorToGroup, id: Number(arg.id) },
        { type: TagTypes.AddAuthorToGroup, id: "PARTIAL-LIST" },
        { type: TagTypes.AddAuthorToGroup, id: "LIST" },
      ],
    }),
    acceptAddAuthorToGroupRequest: builder.mutation<AddAuthorToGroupModel, number>({
      query: (requestId) => ({
        url: `/authorGroup/author/addRequest/accept/${requestId}`,
        method: "PUT",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: TagTypes.AddAuthorToGroup, id: Number(arg) },
        { type: TagTypes.AddAuthorToGroup, id: "PARTIAL-LIST" },
        { type: TagTypes.AddAuthorToGroup, id: "LIST" },
      ],
    }),
    rejectAddAuthorToGroupRequest: builder.mutation<AddAuthorToGroupModel, CommonRejectionModel>({
      query: (model) => ({
        url: `/authorGroup/author/addRequest/reject/${model.id}`,
        method: "PUT",
        body: model,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: TagTypes.AddAuthorToGroup, id: Number(arg.id) },
        { type: TagTypes.AddAuthorToGroup, id: "PARTIAL-LIST" },
        { type: TagTypes.AddAuthorToGroup, id: "LIST" },
      ],
    }),
    removeAddAuthorToGroupRequest: builder.mutation({
      query: (requestId: number) => ({
        url: `/authorGroup/author/addRequest/${requestId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              { type: TagTypes.AddAuthorToGroup, id: arg },
              { type: TagTypes.AddAuthorToGroup, id: "PARTIAL-LIST" },
              { type: TagTypes.AddAuthorToGroup, id: "LIST" },
            ],
    }),

    deleteMultiAddAuthorToGroupRequests: builder.mutation({
      query: (multiDeleteModel: CommonMultiDeleteModel) => ({
        url: "/authorGroup/author/addRequest/multiDelete",
        method: "DELETE",
        body: multiDeleteModel,
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              ...arg.ids.map((id: number) => ({
                type: TagTypes.AddAuthorToGroup,
                id,
              })),
              { type: TagTypes.AddAuthorToGroup, id: "LIST" },
              { type: TagTypes.AddAuthorToGroup, id: "PARTIAL-LIST" },
            ],
    }),

    // Article
    getArticles: builder.query({
      query: () => "/article",
      providesTags: (result = [], error, arg) => [{ type: TagTypes.Article, id: "LIST" }],
    }),
    getArticleCommonInfo: builder.query<ArticleCommonInfoModel, undefined>({
      query: () => "/article/commonInfo",
      providesTags: [{ type: TagTypes.Article, id: "LIST" }],
    }),
    getArticlesFilteredPaged: builder.query<FilteredPagedQueryResult<ArticleModel>, FilteredPagedModel>({
      query: ({ page = 1, pageSize = 10, searchTerm = "", sortFieldName = "", descending = false, ...rest }: FilteredPagedModel) => {
        let url = `/article/pageable?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}&sortFieldName=${sortFieldName}&descending=${descending}`;
        const restParams = getQuerySearchParams(rest);
        if (restParams) {
          url += `&${restParams}`;
        }

        return url;
      },
      providesTags: (result, error, arg) =>
        result
          ? [
              { type: TagTypes.Article, id: "PARTIAL-LIST" },
              ...result.data.map(({ id }: { id: number | string }) => ({
                type: TagTypes.Article,
                id: Number(id),
              })),
            ]
          : [{ type: TagTypes.Article, id: "PARTIAL-LIST" }],
    }),
    getArticle: builder.query<ArticleModel, number>({
      query: (articleId) => `/article/${articleId}`,
      providesTags: (result, error, arg) => [{ type: TagTypes.Article, id: arg }],
    }),
    addArticle: builder.mutation({
      query: (initialArticle) => ({
        url: "/article",
        method: "POST",
        body: initialArticle,
      }),
      invalidatesTags: [
        { type: TagTypes.Article, id: "LIST" },
        { type: TagTypes.Article, id: "PARTIAL-LIST" },
        { type: TagTypes.Author, id: "LIST" },
        { type: TagTypes.Author, id: "PARTIAL-LIST" },
      ],
    }),
    editArticle: builder.mutation({
      query: (article) => ({
        url: `/article/${article.id}`,
        method: "PUT",
        body: article,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: TagTypes.Article, id: Number(arg.id) },
        { type: TagTypes.Author, id: "LIST" },
        { type: TagTypes.Author, id: "PARTIAL-LIST" },
      ],
    }),

    switchActiveArticle: builder.mutation({
      query: (model: CommonEntityActiveSwitch) => ({
        url: `/article/switchActive/${model.id}`,
        method: "PUT",
        body: model,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: TagTypes.Article, id: arg.id },
        { type: TagTypes.Article, id: "LIST" },
      ],
    }),

    publishingArticle: builder.mutation({
      query: (articleId: number) => ({
        url: `/article/publishing/${articleId}`,
        method: "PUT",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: TagTypes.Article, id: Number(arg) },
        { type: TagTypes.Article, id: "LIST" },
      ],
    }),
    publishingCancelArticle: builder.mutation({
      query: (articleId: number) => ({
        url: `/article/publishing/cancel/${articleId}`,
        method: "PUT",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: TagTypes.Article, id: Number(arg) },
        { type: TagTypes.Article, id: "LIST" },
      ],
    }),
    publishArticle: builder.mutation({
      query: (articleId: number) => ({
        url: `/article/publish/${articleId}`,
        method: "PUT",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: TagTypes.Article, id: Number(arg) },
        { type: TagTypes.Article, id: "LIST" },
      ],
    }),
    publishRejectArticle: builder.mutation({
      query: (model: ArticleReasonModel) => ({
        url: `/article/publish/reject/${model.articleId}`,
        method: "PUT",
        body: { reason: model.reason },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: TagTypes.Article, id: Number(arg.articleId) },
        { type: TagTypes.Article, id: "LIST" },
      ],
    }),
    reviseRequestArticle: builder.mutation({
      query: (model: ArticleReasonModel) => ({
        url: `/article/reviseRequest/${model.articleId}`,
        method: "PUT",
        body: { reason: model.reason },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: TagTypes.Article, id: Number(arg.articleId) },
        { type: TagTypes.Article, id: "LIST" },
      ],
    }),
    reviseRequestCancelArticle: builder.mutation({
      query: (articleId: number) => ({
        url: `/article/reviseRequest/cancel/${articleId}`,
        method: "PUT",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: TagTypes.Article, id: Number(arg) },
        { type: TagTypes.Article, id: "LIST" },
      ],
    }),
    reviseArticle: builder.mutation({
      query: (articleId: number) => ({
        url: `/article/revise/${articleId}`,
        method: "PUT",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: TagTypes.Article, id: Number(arg) },
        { type: TagTypes.Article, id: "LIST" },
      ],
    }),
    reviseRejectArticle: builder.mutation({
      query: (model: ArticleReasonModel) => ({
        url: `/article/revise/reject/${model.articleId}`,
        method: "PUT",
        body: { reason: model.reason },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: TagTypes.Article, id: Number(arg.articleId) },
        { type: TagTypes.Article, id: "LIST" },
      ],
    }),
    deleteArticle: builder.mutation({
      query: (articleId: number) => ({
        url: `/article/${articleId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              { type: TagTypes.Article, id: arg },
              { type: TagTypes.Article, id: "LIST" },
              { type: TagTypes.Author, id: "LIST" },
              { type: TagTypes.Author, id: "PARTIAL-LIST" },
            ],
    }),
    deleteMultiArticles: builder.mutation({
      query: (multiDeleteModel: CommonMultiDeleteModel) => ({
        url: "/article/multiDelete",
        method: "DELETE",
        body: multiDeleteModel,
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              ...arg.ids.map((id: number) => ({
                type: TagTypes.Article,
                id,
              })),
              { type: TagTypes.Article, id: "LIST" },
              { type: TagTypes.Author, id: "LIST" },
              { type: TagTypes.Author, id: "PARTIAL-LIST" },
            ],
    }),

    // BankAccount

    getBankAccounts: builder.query<BankAccountModel[], undefined>({
      query: () => "/bankAccount",
      providesTags: (result = [], error, arg) => [{ type: TagTypes.BankAccount, id: "LIST" }],
    }),
    getBankAccountCommonInfo: builder.query<EntityCommonInfoModel, undefined>({
      query: () => "/bankAccount/commonInfo",
      providesTags: [{ type: TagTypes.BankAccount, id: "LIST" }],
    }),
    getBankAccountsFilteredPaged: builder.query<FilteredPagedQueryResult<BankAccountModel>, FilteredPagedModel>({
      query: ({ page = 1, pageSize = 10, searchTerm = "", sortFieldName = "", descending = false, ...rest }: FilteredPagedModel) => {
        let url = `/bankAccount/pageable?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}&sortFieldName=${sortFieldName}&descending=${descending}`;
        const restParams = getQuerySearchParams(rest);
        if (restParams) {
          url += `&${restParams}`;
        }

        return url;
      },
      providesTags: (result, error, arg) =>
        result
          ? [
              { type: TagTypes.BankAccount, id: "PARTIAL-LIST" },
              ...result.data.map(({ id }: { id: number | string }) => ({
                type: TagTypes.BankAccount,
                id: Number(id),
              })),
            ]
          : [{ type: TagTypes.BankAccount, id: "PARTIAL-LIST" }],
    }),
    getBankAccount: builder.query<ArticleSubCategoryModel, number>({
      query: (bankAccountId) => `/bankAccount/${bankAccountId}`,
      providesTags: (result, error, arg) => [{ type: TagTypes.BankAccount, id: arg }],
    }),
    addBankAccount: builder.mutation<BankAccountModel, BankAccountModel>({
      query: (model) => ({
        url: "/bankAccount",
        method: "POST",
        body: model,
      }),
      invalidatesTags: [
        { type: TagTypes.BankAccount, id: "LIST" },
        { type: TagTypes.BankAccount, id: "PARTIAL-LIST" },
      ],
    }),
    editBankAccount: builder.mutation<BankAccountModel, BankAccountModel>({
      query: (model) => ({
        url: `/bankAccount/${model.id}`,
        method: "PUT",
        body: model,
      }),
      invalidatesTags: (result, error, arg) => [{ type: TagTypes.BankAccount, id: Number(arg.id) }],
    }),
    switchActiveBankAccount: builder.mutation({
      query: (model: CommonEntityActiveSwitch) => ({
        url: `/bankAccount/switchActive/${model.id}`,
        method: "PUT",
        body: model,
      }),
      invalidatesTags: (result, error, arg) => [{ type: TagTypes.BankAccount, id: arg.id }],
    }),
    deleteBankAccount: builder.mutation({
      query: (bankAccountId: number) => ({
        url: `/bankAccount/${bankAccountId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              { type: TagTypes.BankAccount, id: arg },
              { type: TagTypes.BankAccount, id: "LIST" },
              { type: TagTypes.BankAccount, id: "PARTIAL-LIST" },
            ],
    }),
    deleteMultiBankAccounts: builder.mutation({
      query: (multiDeleteModel: CommonMultiDeleteModel) => ({
        url: "/bankAccount/multiDelete",
        method: "DELETE",
        body: multiDeleteModel,
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              ...arg.ids.map((id: number) => ({
                type: TagTypes.BankAccount,
                id,
              })),
              { type: TagTypes.BankAccount, id: "LIST" },
              { type: TagTypes.BankAccount, id: "PARTIAL-LIST" },
            ],
    }),

    // AuthorCooperationRequest

    getAuthorCooperationRequestCommonInfo: builder.query<EntityCommonInfoModel, undefined>({
      query: () => "/authorCooperationRequest/commonInfo",
      providesTags: [{ type: TagTypes.AuthorCooperationRequest, id: "LIST" }],
    }),
    getAuthorCooperationRequestsFilteredPaged: builder.query<FilteredPagedQueryResult<AuthorCooperationRequestModel>, FilteredPagedModel>({
      query: ({ page = 1, pageSize = 10, searchTerm = "", sortFieldName = "", descending = false, ...rest }: FilteredPagedModel) => {
        let url = `/authorCooperationRequest/pageable?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}&sortFieldName=${sortFieldName}&descending=${descending}`;
        const restParams = getQuerySearchParams(rest);
        if (restParams) {
          url += `&${restParams}`;
        }

        return url;
      },
      providesTags: (result, error, arg) =>
        result
          ? [
              { type: TagTypes.AuthorCooperationRequest, id: "PARTIAL-LIST" },
              ...result.data.map(({ id }: { id: number | string }) => ({
                type: TagTypes.AuthorCooperationRequest,
                id: Number(id),
              })),
            ]
          : [{ type: TagTypes.AuthorCooperationRequest, id: "PARTIAL-LIST" }],
    }),
    getAuthorCooperationRequest: builder.query<AuthorCooperationRequestModel, number>({
      query: (requestId) => `/authorCooperationRequest/${requestId}`,
      providesTags: (result, error, arg) => [{ type: TagTypes.AuthorCooperationRequest, id: arg }],
    }),
    addAuthorCooperationRequest: builder.mutation<AuthorCooperationRequestModel, AuthorCooperationRequestModel>({
      query: (model) => ({
        url: "/authorCooperationRequest",
        method: "POST",
        body: model,
      }),
      invalidatesTags: [
        { type: TagTypes.AuthorCooperationRequest, id: "LIST" },
        { type: TagTypes.AuthorCooperationRequest, id: "PARTIAL-LIST" },
      ],
    }),
    editAuthorCooperationRequest: builder.mutation<AuthorCooperationRequestModel, AuthorCooperationRequestModel>({
      query: (model) => ({
        url: `/authorCooperationRequest/${model.id}`,
        method: "PUT",
        body: model,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: TagTypes.AuthorCooperationRequest, id: Number(arg.id) },
        { type: TagTypes.AuthorCooperationRequest, id: "LIST" },
      ],
    }),
    acceptAuthorCooperationRequest: builder.mutation<AuthorCooperationRequestModel, number>({
      query: (requestId) => ({
        url: `/authorCooperationRequest/accept/${requestId}`,
        method: "PUT",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: TagTypes.AuthorCooperationRequest, id: Number(arg) },
        { type: TagTypes.AuthorCooperationRequest, id: "LIST" },
      ],
    }),
    rejectAuthorCooperationRequest: builder.mutation<AuthorCooperationRequestModel, CommonRejectionModel>({
      query: (model) => ({
        url: `/authorCooperationRequest/reject/${model.id}`,
        method: "PUT",
        body: model,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: TagTypes.AuthorCooperationRequest, id: Number(arg.id) },
        { type: TagTypes.AuthorCooperationRequest, id: "LIST" },
      ],
    }),
    deleteAuthorCooperationRequest: builder.mutation({
      query: (requestId: number) => ({
        url: `/authorCooperationRequest/${requestId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              { type: TagTypes.AuthorCooperationRequest, id: arg },
              { type: TagTypes.AuthorCooperationRequest, id: "LIST" },
            ],
    }),
    deleteMultiAuthorCooperationRequests: builder.mutation({
      query: (multiDeleteModel: CommonMultiDeleteModel) => ({
        url: "/authorCooperationRequest/multiDelete",
        method: "DELETE",
        body: multiDeleteModel,
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              ...arg.ids.map((id: number) => ({
                type: TagTypes.AuthorCooperationRequest,
                id,
              })),
              { type: TagTypes.AuthorCooperationRequest, id: "LIST" },
            ],
    }),

    // ArticleContract
    getArticleContracts: builder.query<ArticleContractModel[], undefined>({
      query: () => "/articleContract",
      providesTags: (result = [], error, arg) => [{ type: TagTypes.ArticleContract, id: "LIST" }],
    }),

    getArticleContractCommonInfo: builder.query<EntityCommonInfoModel, undefined>({
      query: () => "/articleContract/commonInfo",
      providesTags: [{ type: TagTypes.ArticleContract, id: "LIST" }],
    }),
    getArticleContractsFilteredPaged: builder.query<FilteredPagedQueryResult<ArticleContractModel>, FilteredPagedModel>({
      query: ({ page = 1, pageSize = 10, searchTerm = "", sortFieldName = "", descending = false, ...rest }: FilteredPagedModel) => {
        let url = `/articleContract/pageable?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}&sortFieldName=${sortFieldName}&descending=${descending}`;
        const restParams = getQuerySearchParams(rest);
        if (restParams) {
          url += `&${restParams}`;
        }

        return url;
      },
      providesTags: (result, error, arg) =>
        result
          ? [
              { type: TagTypes.ArticleContract, id: "PARTIAL-LIST" },
              ...result.data.map(({ id }: { id: number | string }) => ({
                type: TagTypes.ArticleContract,
                id: Number(id),
              })),
            ]
          : [{ type: TagTypes.ArticleContract, id: "PARTIAL-LIST" }],
    }),
    getArticleContract: builder.query<ArticleContractModel, number>({
      query: (contractId) => `/articleContract/${contractId}`,
      providesTags: (result, error, arg) => [{ type: TagTypes.ArticleContract, id: arg }],
    }),
    addArticleContract: builder.mutation<ArticleContractModel, ArticleContractModel>({
      query: (model) => ({
        url: "/articleContract",
        method: "POST",
        body: model,
      }),
      invalidatesTags: [
        { type: TagTypes.ArticleContract, id: "LIST" },
        { type: TagTypes.ArticleContract, id: "PARTIAL-LIST" },
      ],
    }),
    editArticleContract: builder.mutation<ArticleContractModel, ArticleContractModel>({
      query: (model) => ({
        url: `/articleContract/${model.id}`,
        method: "PUT",
        body: model,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: TagTypes.ArticleContract, id: Number(arg.id) },
        { type: TagTypes.ArticleContract, id: "LIST" },
        { type: TagTypes.ArticleContract, id: "PARTIAL-LIST" },
      ],
    }),
    acceptArticleContract: builder.mutation<ArticleContractModel, number>({
      query: (contractId) => ({
        url: `/articleContract/accept/${contractId}`,
        method: "PUT",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: TagTypes.ArticleContract, id: Number(arg) },
        { type: TagTypes.ArticleContract, id: "PARTIAL-LIST" },
        { type: TagTypes.ArticleContract, id: "LIST" },
      ],
    }),
    closeArticleContract: builder.mutation<ArticleContractModel, number>({
      query: (contractId) => ({
        url: `/articleContract/close/${contractId}`,
        method: "PUT",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: TagTypes.ArticleContract, id: Number(arg) },
        { type: TagTypes.ArticleContract, id: "PARTIAL-LIST" },
        { type: TagTypes.ArticleContract, id: "LIST" },
      ],
    }),
    deleteArticleContract: builder.mutation({
      query: (contractId: number) => ({
        url: `/articleContract/${contractId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              { type: TagTypes.ArticleContract, id: arg },
              { type: TagTypes.ArticleContract, id: "LIST" },
              { type: TagTypes.ArticleContract, id: "PARTIAL-LIST" },
            ],
    }),
    deleteMultiArticleContracts: builder.mutation({
      query: (multiDeleteModel: CommonMultiDeleteModel) => ({
        url: "/articleContract/multiDelete",
        method: "DELETE",
        body: multiDeleteModel,
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              ...arg.ids.map((id: number) => ({
                type: TagTypes.ArticleContract,
                id,
              })),
              { type: TagTypes.ArticleContract, id: "LIST" },
              { type: TagTypes.ArticleContract, id: "PARTIAL-LIST" },
            ],
    }),
  }),
});

export const {
  useGetArticleCategoriesQuery,
  useGetArticleCategoryCommonInfoQuery,
  useGetArticleCategoriesFilteredPagedQuery,
  useGetArticleCategoryQuery,
  useAddArticleCategoryMutation,
  useEditArticleCategoryMutation,
  useDeleteArticleCategoryMutation,
  useDeleteMultiArticleCategoriesMutation,

  useGetArticleSubCategoriesQuery,
  useGetArticleSubCategoryCommonInfoQuery,
  useGetArticleSubCategoriesFilteredPagedQuery,
  useGetArticleSubCategoryQuery,
  useAddArticleSubCategoryMutation,
  useEditArticleSubCategoryMutation,
  useDeleteArticleSubCategoryMutation,
  useDeleteMultiArticleSubCategoriesMutation,

  useGetAuthorsQuery,
  useGetAuthorCommonInfoQuery,
  useGetAuthorsFilteredPagedQuery,
  useGetAuthorQuery,
  useAddAuthorMutation,
  useEditAuthorMutation,
  useSwitchActiveAuthorMutation,
  useDeleteAuthorMutation,
  useDeleteMultiAuthorsMutation,

  useGetAuthorGroupsQuery,
  useGetAuthorGroupCommonInfoQuery,
  useGetAuthorGroupsFilteredPagedQuery,
  useGetAuthorGroupQuery,
  useAddAuthorGroupMutation,
  useEditAuthorGroupMutation,
  useSwitchActiveAuthorGroupMutation,
  useActivateAuthorGroupMutation,
  useRejectAuthorGroupMutation,
  useDisableAuthorGroupMutation,
  useAddAuthorToGroupMutation,
  useRemoveAuthorFromGroupMutation,
  useDeleteAuthorGroupMutation,
  useDeleteMultiAuthorGroupsMutation,

  useGetAddAuthorToGroupRequestCommonInfoQuery,
  useGetAddAuthorToGroupRequestsFilteredPagedQuery,
  useGetAddAuthorToGroupRequestQuery,
  useAddAddAuthorToGroupRequestMutation,
  useEditAddAuthorToGroupRequestMutation,
  useAcceptAddAuthorToGroupRequestMutation,
  useRejectAddAuthorToGroupRequestMutation,
  useRemoveAddAuthorToGroupRequestMutation,
  useDeleteMultiAddAuthorToGroupRequestsMutation,

  useGetArticlesQuery,
  useGetArticleCommonInfoQuery,
  useGetArticlesFilteredPagedQuery,
  useGetArticleQuery,
  useAddArticleMutation,
  useEditArticleMutation,
  useSwitchActiveArticleMutation,
  usePublishingArticleMutation,
  usePublishingCancelArticleMutation,
  usePublishArticleMutation,
  usePublishRejectArticleMutation,
  useReviseRequestArticleMutation,
  useReviseRequestCancelArticleMutation,
  useReviseArticleMutation,
  useReviseRejectArticleMutation,
  useDeleteArticleMutation,
  useDeleteMultiArticlesMutation,

  useGetBankAccountsQuery,
  useGetBankAccountCommonInfoQuery,
  useGetBankAccountsFilteredPagedQuery,
  useGetBankAccountQuery,
  useAddBankAccountMutation,
  useEditBankAccountMutation,
  useSwitchActiveBankAccountMutation,
  useDeleteBankAccountMutation,
  useDeleteMultiBankAccountsMutation,

  useGetAuthorCooperationRequestCommonInfoQuery,
  useGetAuthorCooperationRequestsFilteredPagedQuery,
  useGetAuthorCooperationRequestQuery,
  useAddAuthorCooperationRequestMutation,
  useEditAuthorCooperationRequestMutation,
  useAcceptAuthorCooperationRequestMutation,
  useRejectAuthorCooperationRequestMutation,
  useDeleteAuthorCooperationRequestMutation,
  useDeleteMultiAuthorCooperationRequestsMutation,

  useGetArticleContractsQuery,
  useGetArticleContractCommonInfoQuery,
  useGetArticleContractsFilteredPagedQuery,
  useGetArticleContractQuery,
  useAddArticleContractMutation,
  useEditArticleContractMutation,
  useAcceptArticleContractMutation,
  useCloseArticleContractMutation,
  useDeleteArticleContractMutation,
  useDeleteMultiArticleContractsMutation,
} = articlesSlice;
